import * as React from 'react';
import { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { largestFirst, sharesOf } from './shares';
import styles from './styles.module.scss';

export interface DonutItem {
  key: string | number;
  label: string;
  count: number;
  color: string;
}

interface Props {
  items: DonutItem[];
  /** Called with the key of a clicked slice; the slices aren't clickable without it. */
  onSelect?: (key: DonutItem['key']) => void;
}

/**
 * Share of each Tema (or chart category) with a legend of percentages, largest first. Slices
 * without Acciones are left out.
 */
const ThemeDonutChart = ({ items, onSelect }: Props) => {
  const { shown, data } = useMemo(() => {
    const sorted = largestFirst(items.filter(item => item.count > 0));
    const shares = sharesOf(sorted.map(item => item.count));
    const withShare = sorted.map((item, i) => ({ ...item, share: shares[i] }));
    return {
      shown: withShare,
      data: {
        labels: withShare.map(item => item.label),
        datasets: [{ data: withShare.map(item => item.count), backgroundColor: withShare.map(item => item.color), borderWidth: 0 }]
      }
    };
  }, [items]);

  const options = useMemo(() => ({
    legend: { display: false },
    cutoutPercentage: 52,
    maintainAspectRatio: false,
    ...(onSelect && {
      onClick: (_: unknown, elements: { _index: number }[]) => elements[0] && onSelect(shown[elements[0]._index].key),
      onHover: (event: { target: EventTarget | null }, elements: unknown[]) => {
        (event.target as HTMLElement).style.cursor = elements.length ? 'pointer' : 'default';
      }
    })
  }), [onSelect, shown]);

  return (
    <div className={styles.donut}>
      <div className={styles.chart}>
        <Doughnut data={data} options={options} />
      </div>
      <ul className={styles.legend}>
        {shown.map(item => (
          <li key={item.key}>
            <span className={styles.swatch} style={{ backgroundColor: item.color }} />
            <span className={styles.label}>{item.label}</span>
            <span className={styles.share}>{item.share}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ThemeDonutChart;
